// qs-store-tools.ts
import type { BrushPreset, BrushType, ColorSwatch, SmoothingMode, Tool } from '@/types/qs-types-index'
import { DEFAULT_BRUSH_SIZE } from '@/config/qs-brushDefaults'
import { useStorage } from '@vueuse/core'
import { defineStore } from 'pinia'
import { computed, ref } from 'vue'

export const useToolStore = defineStore('tools', () => {
  const currentTool = useStorage<Tool>('vectorsketch-current-tool', {
    type: 'brush',
    size: DEFAULT_BRUSH_SIZE,
    color: '#3498db',
    opacity: 1,
    brushType: 'round',
    smoothingMode: 'curved'
  })

  const backgroundColor = useStorage('vectorsketch-background-color', '#ebbcbc')
  const isPanMode = ref(false)

  const colorSwatches = useStorage<ColorSwatch[]>('vectorsketch-color-swatches', [
    { id: 'swatch-1', color: '#000000', name: 'Black' },
    { id: 'swatch-2', color: '#ff0000', name: 'Red' },
    { id: 'swatch-3', color: '#00ff00', name: 'Green' },
    { id: 'swatch-4', color: '#0000ff', name: 'Blue' },
    { id: 'swatch-5', color: '#ffff00', name: 'Yellow' },
    { id: 'swatch-6', color: '#ff00ff', name: 'Magenta' }
  ])

  const brushPresets = useStorage<BrushPreset[]>('vectorsketch-brush-presets', [
    { id: 'brush-1', size: 2, name: 'Fine' },
    { id: 'brush-2', size: 4, name: 'Medium' },
    { id: 'brush-3', size: 8, name: 'Thick' }
  ])

  const toolHistory = ref<Tool[]>([])
  const historyIndex = ref(-1)

  const currentColorSwatch = computed(() =>
    colorSwatches.value.find(swatch => swatch.color === currentTool.value.color)
  )

  const currentBrushPreset = computed(() =>
    brushPresets.value.find(preset => preset.size === currentTool.value.size)
  )

  const setTool = (tool: Partial<Tool>) => {
    if (historyIndex.value < toolHistory.value.length - 1) {
      toolHistory.value = toolHistory.value.slice(0, historyIndex.value + 1)
    }

    toolHistory.value.push({ ...currentTool.value })
    historyIndex.value = toolHistory.value.length - 1

    currentTool.value = { ...currentTool.value, ...tool }
    window.dispatchEvent(new CustomEvent('tool-changed'))
  }

  const setColor = (color: string) => {
    setTool({ color })
  }

  const setBrushSize = (size: number) => {
    setTool({ size })
  }

  const setToolType = (type: Tool['type']) => {
    setTool({ type })
  }

  const setOpacity = (opacity: number) => {
    setTool({ opacity: Math.max(0, Math.min(1, opacity)) })
  }

  const setBrushType = (brushType: BrushType) => {
    setTool({ brushType })
  }

  const setSmoothingMode = (smoothingMode: SmoothingMode) => {
    setTool({ smoothingMode })
  }

  const setBackgroundColor = (color: string) => {
    let hexColor = color

    if (color.startsWith('rgba(')) {
      const rgbaMatch = color.match(/rgba\((\d+),\s*(\d+),\s*(\d+),\s*[\d.]+\)/)
      if (rgbaMatch) {
        const r = parseInt(rgbaMatch[1] || '0')
        const g = parseInt(rgbaMatch[2] || '0')
        const b = parseInt(rgbaMatch[3] || '0')
        hexColor = `#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${b.toString(16).padStart(2, '0')}`
      }
    } else if (color.startsWith('rgb(')) {
      const rgbMatch = color.match(/rgb\((\d+),\s*(\d+),\s*(\d+)\)/)
      if (rgbMatch) {
        const r = parseInt(rgbMatch[1] || '0')
        const g = parseInt(rgbMatch[2] || '0')
        const b = parseInt(rgbMatch[3] || '0')
        hexColor = `#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${b.toString(16).padStart(2, '0')}`
      }
    }

    backgroundColor.value = hexColor
  }

  const setPanMode = (enabled: boolean) => {
    isPanMode.value = enabled
    window.dispatchEvent(new CustomEvent('tool-changed'))
  }

  const togglePanMode = () => {
    isPanMode.value = !isPanMode.value
    window.dispatchEvent(new CustomEvent('tool-changed'))
  }

  const updateColorSwatch = (swatchId: string, color: string) => {
    const swatch = colorSwatches.value.find(s => s.id === swatchId)
    if (swatch) {
      swatch.color = color
    }
  }

  const addColorSwatch = (color: string, name?: string) => {
    const newSwatch: ColorSwatch = {
      id: `swatch-${Date.now()}`,
      color,
      name: name || `Color ${colorSwatches.value.length + 1}`
    }
    colorSwatches.value.push(newSwatch)
  }

  const removeColorSwatch = (swatchId: string) => {
    const index = colorSwatches.value.findIndex(s => s.id === swatchId)
    if (index !== -1) {
      colorSwatches.value.splice(index, 1)
    }
  }

  const addBrushPreset = (size: number, name?: string) => {
    const newPreset: BrushPreset = {
      id: `brush-${Date.now()}`,
      size,
      name: name || `Brush ${brushPresets.value.length + 1}`
    }
    brushPresets.value.push(newPreset)
  }

  const removeBrushPreset = (presetId: string) => {
    const index = brushPresets.value.findIndex(p => p.id === presetId)
    if (index !== -1) {
      brushPresets.value.splice(index, 1)
    }
  }

  const undo = () => {
    if (historyIndex.value > 0) {
      historyIndex.value--
      const tool = toolHistory.value[historyIndex.value]
      if (tool) {
        currentTool.value = { ...tool }
      }
    }
  }

  const redo = () => {
    if (historyIndex.value < toolHistory.value.length - 1) {
      historyIndex.value++
      const tool = toolHistory.value[historyIndex.value]
      if (tool) {
        currentTool.value = { ...tool }
      }
    }
  }

  const resetTool = () => {
    currentTool.value = {
      type: 'brush',
      size: DEFAULT_BRUSH_SIZE,
      color: '#3498db',
      opacity: 1,
      brushType: 'round',
      smoothingMode: 'curved'
    }
    toolHistory.value = []
    historyIndex.value = -1
  }

  const getToolForExport = () => {
    return {
      ...currentTool.value,
      backgroundColor: backgroundColor.value
    }
  }

  return {
    currentTool,
    backgroundColor,
    isPanMode,
    colorSwatches,
    brushPresets,
    toolHistory,
    historyIndex,
    currentColorSwatch,
    currentBrushPreset,
    setTool,
    setColor,
    setBrushSize,
    setToolType,
    setOpacity,
    setBrushType,
    setSmoothingMode,
    setBackgroundColor,
    setPanMode,
    togglePanMode,
    updateColorSwatch,
    addColorSwatch,
    removeColorSwatch,
    addBrushPreset,
    removeBrushPreset,
    undo,
    redo,
    resetTool,
    getToolForExport
  }
})